<!-- @catalog Inline-editable tag chip: rename in place, archive and delete. The TypeScript successor to the legacy `components/global/EditableTag.vue`; use this one for new code. For a read-only tag use CBadge. -->
<template>
  <q-chip
    class="!tw-m-0 !tw-bg-gray-200/75"
    text-color="grey-800"
    :clickable="false"
    :ripple="false"
  >
    <q-form
      class="tw-flex tw-items-center tw-gap-4"
      @submit="handleUpdate(tag)"
    >
      <q-input
        v-if="edit"
        ref="input"
        v-model="_model"
        borderless
        @keyup.esc="cancel"
      />
      <span v-if="!edit">{{ model }}</span>

      <q-button-group>
        <c-button-icon
          v-if="edit"
          class="!tw-p-2"
          size="sm"
          color="transparent"
          text-color="negative"
          icon="fas fa-times"
          @click="cancel"
        />

        <c-button-icon
          v-if="!tag.meta?.archived"
          class="!tw-p-2"
          size="sm"
          color="transparent"
          :text-color="edit ? 'positive' : 'primary'"
          :icon="
            edit
              ? toClassicIcon(saveIcon, 'solid')
              : toClassicIcon(editIcon, 'solid')
          "
          @click="handleUpdate(tag)"
        />

        <c-button-icon
          v-if="!edit && archivable"
          class="!tw-p-2"
          size="sm"
          color="transparent"
          :text-color="tag.meta?.archived ? 'positive' : 'warning'"
          :tooltip="archiveTooltip"
          :icon="toClassicIcon(archiveIcon, 'solid')"
          @click="archiveTag(tag)"
        />

        <c-button-icon
          v-if="!edit"
          class="!tw-p-2"
          size="sm"
          color="transparent"
          text-color="negative"
          :tooltip="deleteTooltip"
          :icon="toClassicIcon(deleteIcon, 'solid')"
          @click="deleteTag(tag)"
        />
      </q-button-group>
    </q-form>
  </q-chip>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import {
  toClassicIcon,
  archiveIcon,
  deleteIcon,
  editIcon,
  saveIcon,
} from "common/defaultIcons";
import type { EditableTagItem } from "./editableTag.types";
import type { QInput } from "quasar";

// Model
const model = defineModel<string>();

// Props
const props = withDefaults(
  defineProps<{
    tag: EditableTagItem;
    archivable?: boolean;
    entityLabel?: string;
  }>(),
  {
    archivable: false,
    entityLabel: "Tag",
  },
);

// Emits
const emit = defineEmits<{
  (event: "update", payload: { id: number; value: string }): void;
  (event: "delete", payload: { id: number }): void;
  (event: "archive", payload: { id: number }): void;
}>();

// Refs
const _model = ref<string>("");
const edit = ref<boolean>(false);
const input = ref<QInput | null>(null);

const cancel = (): void => {
  edit.value = false;

  // Change the model back to the original value
  _model.value = model.value ?? "";
};

const handleUpdate = (tag: EditableTagItem): void => {
  if (edit.value) {
    emit("update", {
      id: tag.id,
      value: _model.value,
    });
  }

  if (edit.value === false) {
    // Start from the current value: the parent may have changed it since mount.
    _model.value = model.value ?? "";
  }

  edit.value = !edit.value;
};

const deleteTag = (tag: EditableTagItem): void => {
  emit("delete", {
    id: tag.id,
  });
};

const archiveTag = (tag: EditableTagItem): void => {
  emit("archive", {
    id: tag.id,
  });
};

watch(
  () => edit.value,
  (value) => {
    if (value) {
      setTimeout(() => {
        input.value?.focus();
        input.value?.select();
      }, 50);
    }
  },
);

onBeforeMount(() => {
  _model.value = model.value ?? "";
});

const deleteTooltip = computed<string>(
  () => `${props.entityLabel} verwijderen`,
);

const archiveTooltip = computed<string>(() =>
  props.tag.meta?.archived
    ? `${props.entityLabel} dearchiveren`
    : `${props.entityLabel} archiveren zodat deze in de toekomst niet meer gebruikt kan worden`,
);
</script>

<style lang="scss" scoped>
.q-chip {
  // Rendered inside QChip, so it never carries this component's scope id.
  :deep(.q-chip__content) {
    @apply tw-flex tw-gap-4;
  }
}
</style>
